// 匿名プレイヤーID (localStorage 永続) とニックネームの管理。
// サインイン不要。IDはクライアント生成の UUID。
//
// **キーは `hongsoft.*`（ゲーム名ではなくポータル名）**。プレイヤーは HONGSOFT.JP GAMES
// 全体で1人であり、二角取り固有のデータではないため。ゲーム固有のデータ
// （`nikakudori.progress` / `nikakudori.continue.*` / 設定）は `nikakudori.*` のまま。
//
// ※ 移行コードは持たない。`games.hongsoft.jp` へ移った時点で localStorage は
//   どのみちオリジンごと変わる（＝旧キーは読めない）ので、書き換える意味が無い。

import { SUPPORTED_LANGS, SOURCE_LANG, isLang, type Lang } from '@/platform/i18n';
// ⚠️ **`platform/` からゲーム側を参照する2箇所目**（もう1つは settings.ts の設定スライス）。
//    どちらも「登録簿から導出する」ためのもので、registry は軽い情報しか静的に持たないので
//    Phaser は落ちてこない。**ゲームを名指しする import はここでも作らない**。
import { GAME_STORAGE_PREFIXES } from '@/games/registry';

/** ポータル横断データの接頭辞。プレイヤーは HONGSOFT.JP GAMES 全体で1人。 */
const PORTAL_PREFIX = 'hongsoft.';

const PLAYER_ID_KEY = 'hongsoft.playerId';
const NICKNAME_KEY = 'hongsoft.nickname';
const COUNTRY_KEY = 'hongsoft.country';
// 表示言語。**国コードとは別に保持する**（住んでいる国と読める言語は一致しない）。
// 国と同じくポータル全体で1つのプレイヤー属性なので `hongsoft.*`。
const LANG_KEY = 'hongsoft.lang';

function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // フォールバック (古い環境向け)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** 匿名プレイヤーID を取得（無ければ生成して保存）。 */
export function getPlayerId(): string {
  let id = localStorage.getItem(PLAYER_ID_KEY);
  if (!id) {
    id = generateUuid();
    localStorage.setItem(PLAYER_ID_KEY, id);
  }
  return id;
}

/**
 * ログインで「別のプレイヤーになる」ときだけ使う（[passkey.ts](passkey.ts)）。
 *
 * ⚠️ **これを呼ぶとこの端末の持ち主が入れ替わる**。ゲームの記録は `<gameKey>.*` に
 *    別で残っているので、**呼ぶ側が記録の扱い（統合するか捨てるか）を決めてから**呼ぶこと。
 *    決めないまま呼ぶと、前の持ち主の記録が新しいプレイヤーのものとして同期されうる。
 */
export function setPlayerId(id: string): void {
  localStorage.setItem(PLAYER_ID_KEY, id);
}

export function getNickname(): string | null {
  return localStorage.getItem(NICKNAME_KEY);
}

export function setNickname(name: string): void {
  localStorage.setItem(NICKNAME_KEY, name);
}

/** 国コード (ISO 3166-1 alpha-2, 大文字2字)。未登録なら null。 */
export function getCountry(): string | null {
  return localStorage.getItem(COUNTRY_KEY);
}

export function setCountry(code: string): void {
  localStorage.setItem(COUNTRY_KEY, code.toUpperCase());
}

/** プレイヤー登録が完了しているか（ニックネームがある）。 */
export function isRegistered(): boolean {
  const n = getNickname();
  return !!n && n.trim().length > 0;
}

/**
 * ブラウザの言語設定から対応言語を推定する。
 * **ゲストは登録画面を通らない**（＝言語を選ぶ機会が無い）ので、既定はここで決まる。
 * `navigator.languages` を優先順に見て、最初に対応しているものを採る。
 */
function detectLanguage(): Lang {
  const cands = typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language]);
  for (const raw of cands) {
    if (!raw) continue;
    const primary = raw.toLowerCase().split('-')[0]!; // 'en-US' → 'en'
    const hit = SUPPORTED_LANGS.find((l) => l === primary);
    if (hit) return hit;
  }
  return SOURCE_LANG;
}

/**
 * 表示言語。未設定ならブラウザから推定する（保存はしない＝あとで設定したら素直に切り替わる）。
 * 登録プレイヤーはサーバー(`players.language_code`)にも保存され、端末をまたいで復元される。
 */
export function getLanguage(): Lang {
  const v = localStorage.getItem(LANG_KEY);
  return isLang(v) ? v : detectLanguage();
}

/** 表示言語を保存（未対応コードは無視）。 */
export function setLanguage(lang: string): void {
  if (!isLang(lang)) return;
  localStorage.setItem(LANG_KEY, lang);
}

/**
 * この端末に残っている本サービスのデータを**すべて**消す（プライバシーポリシー 8.）。
 *
 * キーを列挙せず**接頭辞で掃く**のが要点。`hongsoft.*`（ポータル横断）と
 * 各ゲームの `<gameKey>.*`（中断データを含む）が本サービスの持ち物のすべてで、
 * 個別に列挙すると**後から足したキーを消し忘れる**（消し忘れは削除機能の嘘になる）。
 *
 * ゲーム側の接頭辞は**登録簿から導出する**（`GAME_STORAGE_PREFIXES`）。
 * ここに手で書き足す方式だと、ゲームを増やしたときの**書き足し忘れがそのまま
 * プライバシーポリシーの嘘になる**——手順書に頼らず、登録した時点で消える形にした。
 */
export function clearLocalPlayerData(): void {
  // 走査中に消すと index がずれるので、集めてから消す。
  for (const key of ownedKeys()) localStorage.removeItem(key);
}

/** 消すものが1つでもあるか（無いなら「データを削除」を出す意味がない）。 */
export function hasLocalPlayerData(): boolean {
  return ownedKeys().length > 0;
}

// ※ かつてここに `hasLocalGameData()`（`<gameKey>.*` のキーが1つでもあるか）があった。
//   ログインボタンを出すかどうかの門番だったが、統合（docs/AUTH.md 実装順序(5)）が入って
//   **キーの有無ではなく実際に持ち込む記録の件数**で判断するようになったので消した
//   （`platform/net/merge.ts` の `hasContributions`）。キーはあるが中身が空、という
//   状態を「記録あり」と数えてしまうのが具合の悪いところだった。

/** 本サービスが所有する localStorage キー。接頭辞で判定する（`clearLocalPlayerData` 参照）。 */
function ownedKeys(): string[] {
  const prefixes = [PORTAL_PREFIX, ...GAME_STORAGE_PREFIXES];
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && prefixes.some((p) => key.startsWith(p))) keys.push(key);
  }
  return keys;
}
